import { useMemo, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import {
  Box,
  Stack,
  Button,
  Checkbox,
  Paper,
  InputBase,
  Collapse,
  Skeleton,
  CircularProgress,
  Typography,
  Backdrop,
} from '@mui/material';
import { SearchOutlined, Clear } from '@mui/icons-material';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

// Components
import ConfirmationModal from '../../ConfirmationModal/Confirmation';
import CollapsibleTable from '../../../components/CollapsibleTable/CollapsibleTableContainer';
import ApiResponseModal from '../../ApiResponseModal/ApiResponse';
import ChangePrinter from '../../ChangePrinterModal/ChangePrinter';
import UnexpectedItem from '../../UnexpectedItemModal/UnexpectedItem';
import ForceCloseModal from '../../ForceCloseModal/ForceCloseModal';
import { H1, P2 } from '../../../components/HelperComponents/Typography';
import {
  DateField,
  NumberField,
} from '../../../components/HelperComponents/InputFields';

// Services
import { acquireAccessToken, callMsGraph } from '../../../services/msGraph';
import OrderServices from '../../../services/orders';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  resetFilterList,
  searchOrders,
  getProductList,
  resetProductList,
  setPreviousSearch,
  setEmployeeId,
  setRouteStatus,
} from '../../../store/slices/productSlice';
import {
  setRegion,
  resetOrderDetails,
  setCurrentPrinter,
} from '../../../store/slices/orderSlice';

// Utils
import { TranslateDate, IsRouteOpen } from '../../../utils/helpers';

// Hooks
import useModalState from './hooks/useModalState';
import useSearchState from './hooks/useSearchState';
import useApiResponseState from './hooks/useApiResponseState';
import useExpectedState from './hooks/useExpectedState';

// Styled Components
const HeaderBox = styled(Box)`
  border-bottom: 3px solid #00529f;
  padding-bottom: 10px;
  margin-top: 3em;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchBox = styled(Box)`
  align-items: center;
  background-color: #f5f5f5;
  margin-top: 18px;
  margin-bottom: 10px;
  padding: 8px 46px;
  .h2box {
    border-bottom: 3px solid #00529f;
    h2 {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }
  .input-field-container {
    p {
      margin-bottom: 13.82px;
    }
    input {
      padding: 8px 12px 9px;
    }
  }
  .apply-btn {
    width: 100%;
  }
`;

// Utility functions for building structured message data
const buildOrderErrorItems = (orders) => {
  if (!Array.isArray(orders)) return [];
  return orders
    .filter((order) => !order.IsValidOrder)
    .map((order) => ({
      label: order.OrderNumber,
      message: order.OrderErrorMessage,
      children: order.Lines.filter((line) => !line.IsValidLine).map(
        (line) => line.LineErrorMessage
      ),
    }));
};

const buildRouteMessage = ({
  actionType,
  route,
  division,
  success,
  errorText,
  orderErrorItems,
  trnResp,
  isForceClose = false,
}) => {
  const sections = [];

  if (orderErrorItems?.length > 0) {
    sections.push({ title: 'Order Errors', items: orderErrorItems });
  }

  if (trnResp?.IsValid) {
    const successOrders =
      trnResp.Orders?.filter(
        (order) => order.IsValid && order.IsSuccessful !== false
      ) || [];
    if (successOrders.length > 0) {
      sections.push({
        title: 'Auto-Created Transfers',
        items: successOrders.map((order) => ({
          label: `Order: ${order.OrderNumber}`,
          suffix: order.TransferTicket
            ? `Transfer #${order.TransferTicket}`
            : undefined,
          children: order.Skus.filter((sku) => sku.IsValid).map(
            (sku) => `SKU ${sku.SkuNumber} (Qty: ${sku.ReturnQuantity})`
          ),
        })),
      });
    }

    const trnErrorItems =
      trnResp.Orders?.filter(
        (order) => !order.IsValid || order.IsSuccessful === false
      ).map((order) => ({
        label: order.OrderNumber,
        message: order.ErrorMessage || 'Transfer failed',
        children: order.Skus.filter((sku) => !sku.IsValid).map(
          (sku) => `SKU ${sku.SkuNumber} - ${sku.ErrorMessage}`
        ),
      })) || [];

    const hasTrnRouteError =
      trnResp.IsSuccessful === false && !!trnResp.ErrorMessage;

    if (hasTrnRouteError || trnErrorItems.length > 0) {
      sections.push({
        title: 'Transfer Errors',
        message: hasTrnRouteError ? trnResp.ErrorMessage : undefined,
        items: trnErrorItems,
      });
    }
  }

  return {
    prefix: isForceClose ? 'Force' : undefined,
    action: `${actionType} Route ${route} in ${division}`,
    success,
    errorText,
    sections: sections.length > 0 ? sections : undefined,
  };
};

export default function MainPage({ productList, addSKUList, isLoading }) {
  const dispatch = useDispatch();
  const [printerCookie, setPrinterCookie] = useCookies(['printer']);

  // Custom hooks for state management
  const modalState = useModalState();
  const searchState = useSearchState();
  const apiResponseState = useApiResponseState();

  // Redux selectors
  const savedSearch = useSelector(
    (state) => state.product.previousSearchValues
  );
  const expectedState = useExpectedState(dispatch, savedSearch, true);
  const routeStatus = useSelector((state) => state.product.routeStatus);
  const currentPrinter = useSelector((state) => state.order.currentPrinter);
  const employeeId = useSelector((state) => state.product.employeeId);
  const warehouse = useSelector((state) => state.product.warehouseId);
  const routeCloseData = useSelector((state) => state.product.routeCloseData);
  const routeNeedToReturnQuantity = useSelector(
    (state) => state.product.routeNeedToReturnQuantity
  );
  const routeReturnedQuantity = useSelector(
    (state) => state.product.routeReturnedQuantity
  );
  const RTRNCounts = useSelector((state) => state.product.RTRNCounts);
  const productError = useSelector((state) => state.product.error);
  const previousSearchValues = useSelector(
    (state) => state.product.previousSearchValues
  );
  const succesfulLoad = useSelector(
    (state) => state.product.hasLoadedSuccessful
  );
  const isProductsLoading = useSelector(
    (state) => state.product.isProductsLoading
  );
  const isFirstLoad = useSelector((state) => state.product.isFirstLoad);

  const { date: dateState, division } = savedSearch;

  // Computed values
  const isApplyButtonDisabled = useMemo(
    () => searchState.route === '',
    [searchState.route]
  );

  const hasExpectedSkus = useMemo(() => {
    return productList.some((order) =>
      order.SkuData?.some((sku) => sku.IsExpectedSku === true)
    );
  }, [productList]);

  const closeTimestamp =
    dayjs(routeCloseData?.TimeStamp).format('MM/DD/YYYY HH:mm:ss') ?? 'N/A';
  const closeEmployeeName = routeCloseData?.EmployeeName ?? 'N/A';
  const closeEmployeeNumber = routeCloseData?.EmployeeNumber ?? 'N/A';

  // Core search executor — accepts already-resolved route, date, and division
  const executeSearch = async (
    route,
    date,
    division,
    overrideIsExpected = null
  ) => {
    if (!route || !date) return;

    const token = await acquireAccessToken();
    const graphCall = await callMsGraph(token);
    const employeeId = graphCall.employeeId;

    const searchVars = {
      date,
      route,
      division,
      isExpected:
        overrideIsExpected !== null
          ? overrideIsExpected
          : expectedState.isExpected,
    };
    searchState.setSearchInput('');
    dispatch(resetOrderDetails());
    dispatch(resetFilterList());
    dispatch(resetProductList());
    dispatch(setRegion(division));
    dispatch(setPreviousSearch(searchVars));
    dispatch(getProductList(searchVars));
    dispatch(setEmployeeId(employeeId));
  };

  // Called from Apply button / Enter key — uses current textbox values
  const searchProducts = async (overrideIsExpected = null) => {
    const token = await acquireAccessToken();
    const graphCall = await callMsGraph(token);
    const { extensionAttribute3 } = graphCall.onPremisesExtensionAttributes;
    await executeSearch(
      searchState.route,
      TranslateDate(searchState.date),
      extensionAttribute3,
      overrideIsExpected
    );
  };

  // Called internally to refresh the currently applied route/date
  const refreshProducts = async (overrideIsExpected = null) => {
    await executeSearch(
      previousSearchValues.route,
      previousSearchValues.date,
      previousSearchValues.division,
      overrideIsExpected
    );
  };

  const handleRouteKeyDown = async (event) => {
    if (event.key === 'Enter' && !isProductsLoading && !isApplyButtonDisabled) {
      await searchProducts(true);
    }
  };

  const handleClearAll = () => {
    if (isAnyLoading) return;
    console.log('Clearing all filters ', isAnyLoading);
    searchState.resetSearch();
    expectedState.setIsExpected(true);
    // Clear all Redux state data
    dispatch(resetOrderDetails());
    dispatch(resetFilterList());
    dispatch(resetProductList());
    dispatch(
      setPreviousSearch({
        date: '',
        route: '',
        division: '',
        isExpected: true,
      })
    );
    dispatch(setRouteStatus(''));
  };

  const handleIsExpected = async (event) => {
    event?.preventDefault();
    searchState.setSearchInput('');
    const newIsExpected = !expectedState.isExpected;
    expectedState.updateIsExpected(newIsExpected);

    if (!savedSearch.route || !savedSearch.date || productError) return;
    await refreshProducts(newIsExpected);
  };

  // Modal handlers
  const handleOpenPrinterModal = () => modalState.setOpenPrinterModal(true);
  const handleClosePrinterModal = () => modalState.setOpenPrinterModal(false);
  const handleOpenUnexpectedItemModal = () => {
    if (!currentPrinter) {
      apiResponseState.setApiResponseMessage(
        'Please select a printer before adding an unexpected item.'
      );
      modalState.setApiResponseModal(true);
      return;
    }
    modalState.setOpenUnexpectedItemModal(true);
  };
  const handleCloseUnexpectedItemModal = () =>
    modalState.setOpenUnexpectedItemModal(false);

  const handleSubmitPrinterChange = (printer) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 14);
    setPrinterCookie('printer', printer, {
      path: '/',
      expires: expirationDate,
      sameSite: 'strict',
    });
    dispatch(setCurrentPrinter(printer));
    modalState.setOpenPrinterModal(false);
  };

  const handleOpenRouteConfirmation = () => {
    if (!currentPrinter) {
      apiResponseState.setApiResponseMessage(
        'Please select a printer before closing the route.'
      );
      modalState.setApiResponseModal(true);
      return;
    }
    modalState.setCloseRouteConfirmation(true);
  };

  const handleCloseRouteConfirmation = () => {
    modalState.setCloseRouteConfirmation(false);
  };

  const handleForceModalClose = () => {
    modalState.setForceModal(false);
  };

  const handleTryForceClose = async () => {
    handleForceModalClose();
    await handleConfirmCloseRoute(true);
  };

  const handleConfirmCloseRoute = async (forceClose = false) => {
    searchState.setLoading(true);
    modalState.setCloseRouteConfirmation(false);
    try {
      const postData = {
        RouteNumber: previousSearchValues.route,
        Division: division,
        ShipDate: dateState,
        WarehouseNumber: warehouse,
        EV3Number: employeeId,
        Printer: currentPrinter,
        ForceClose: forceClose,
      };
      const response = await OrderServices.closeRoute(postData);

      // Handle non-object Value (e.g. 400 response where Value is a string)
      if (
        typeof response.Value === 'string' ||
        !response.Value?.IsSuccessful === undefined
      ) {
        apiResponseState.setForceCloseMessage(
          buildRouteMessage({
            actionType: 'Close',
            route: previousSearchValues.route,
            division,
            success: false,
            errorText:
              typeof response.Value === 'string'
                ? response.Value
                : 'An unexpected error occurred',
          })
        );
        apiResponseState.setIsForceCloseSuccessful(false);
        apiResponseState.setAllowForceClose(false);
        modalState.setForceModal(true);
        return;
      }

      if (response.Value.IsSuccessful) {
        // dispatch(setRouteStatus('Closed'));
        apiResponseState.setForceCloseMessage(
          buildRouteMessage({
            actionType: 'Close',
            route: previousSearchValues.route,
            division,
            success: true,
            isForceClose: forceClose,
            trnResp: response.Value.TrnResp,
          })
        );
        await refreshProducts();
        apiResponseState.setIsForceCloseSuccessful(true);
      } else {
        apiResponseState.setForceCloseMessage(
          buildRouteMessage({
            actionType: 'Close',
            route: previousSearchValues.route,
            division,
            success: false,
            errorText:
              response.Value.RouteErrorMessage || 'An unknown error occurred',
            orderErrorItems: buildOrderErrorItems(response.Value.Orders),
            trnResp: response.Value.TrnResp,
            isForceClose: forceClose,
          })
        );
        apiResponseState.setIsForceCloseSuccessful(false);
      }
      apiResponseState.setAllowForceClose(!response.Value.IsSuccessful);
      modalState.setForceModal(true);
    } catch (error) {
      apiResponseState.setForceCloseMessage(
        buildRouteMessage({
          actionType: 'Close',
          route: previousSearchValues.route,
          division,
          success: false,
          errorText: error.message || 'An unexpected error occurred',
        })
      );
      apiResponseState.setIsForceCloseSuccessful(false);
      apiResponseState.setAllowForceClose(false);
      modalState.setForceModal(true);
    } finally {
      searchState.setLoading(false);
    }
  };

  const handleSubmitUnexpectedItem = async (category, skus, order) => {
    const unexpectedCategoryValue = category || 'NotSet';
    const unexpectedItemLines = skus.map((item) => ({
      SkuNumber: item.sku,
      ReturnQuantity: item.quantity,
      Unexpected: unexpectedCategoryValue,
    }));
    searchState.setLoading(true);
    try {
      const response = await OrderServices.postReturnOrders(
        division ? division : 'FL',
        warehouse,
        employeeId,
        unexpectedItemLines,
        order ? order : null,
        previousSearchValues.route,
        currentPrinter,
        dateState
      );

      modalState.setOpenUnexpectedItemModal(false);

      // Handle non-object Value (e.g. 400 response where Value is a string)
      if (typeof response.Value === 'string' || !response.Value) {
        apiResponseState.setApiResponseMessage({
          action: 'Unexpected item return',
          success: false,
          errorText:
            typeof response.Value === 'string'
              ? response.Value
              : 'An unexpected error occurred',
        });
      } else if (response.Value.IsSuccessful) {
        apiResponseState.setApiResponseMessage({
          action: 'Unexpected item return',
          success: true,
        });
        await refreshProducts();
      } else {
        const lineErrorItems = unexpectedItemLines.map((line, index) => ({
          label: `Sku: ${line.SkuNumber}`,
          message:
            response.Value.ReturnLines?.[index]?.LineErrorMessage ||
            'Server Error',
        }));
        apiResponseState.setApiResponseMessage({
          action: 'Unexpected item return',
          success: false,
          errorText: `The Order was ${response.Value.IsValidOrder ? 'Valid' : 'Invalid'}. Order Error: ${response.Value.OrderErrorMessage}.`,
          sections: [{ title: 'Errors by Line Item', items: lineErrorItems }],
        });
      }
    } catch (error) {
      modalState.setOpenUnexpectedItemModal(false);
      apiResponseState.setApiResponseMessage({
        action: 'Unexpected item return',
        success: false,
        errorText: error.message || 'An unexpected error occurred',
      });
    } finally {
      searchState.setLoading(false);
      modalState.setApiResponseModal(true);
    }
  };

  const handleOpenPrintRouteConfirmation = () => {
    if (!currentPrinter) {
      apiResponseState.setApiResponseMessage(
        'Please select a printer before printing the route.'
      );
      modalState.setApiResponseModal(true);
      return;
    }
    modalState.setPrintRouteConfirmation(true);
  };

  const handleClosePrintRouteConfirmation = () => {
    modalState.setPrintRouteConfirmation(false);
  };

  const handleConfirmPrintRoute = async () => {
    searchState.setLoading(true);
    modalState.setPrintRouteConfirmation(false);
    const postData = {
      RouteNumber: previousSearchValues.route,
      Division: division,
      ShipDate: dateState,
      WarehouseNumber: warehouse,
      EV3Number: employeeId,
      Printer: currentPrinter,
      ForceClose: false,
    };
    try {
      const response = await OrderServices.printRoute(postData);

      // Handle non-object Value (e.g. 400 response where Value is a string)
      if (typeof response.Value === 'string' || !response.Value) {
        apiResponseState.setApiResponseMessage(
          buildRouteMessage({
            actionType: 'Print Expected on',
            route: previousSearchValues.route,
            division,
            success: false,
            errorText:
              typeof response.Value === 'string'
                ? response.Value
                : 'An unexpected error occurred',
          })
        );
      } else if (response.Value.IsSuccessful) {
        apiResponseState.setApiResponseMessage(
          buildRouteMessage({
            actionType: 'Print Expected on',
            route: previousSearchValues.route,
            division,
            success: true,
          })
        );
      } else {
        apiResponseState.setApiResponseMessage(
          buildRouteMessage({
            actionType: 'Print Expected on',
            route: previousSearchValues.route,
            division,
            success: false,
            errorText:
              response.Value.RouteErrorMessage || 'An unknown error occurred',
            orderErrorItems: buildOrderErrorItems(response.Value.Orders),
          })
        );
      }
    } catch (error) {
      apiResponseState.setApiResponseMessage(
        buildRouteMessage({
          actionType: 'Print Expected on',
          route: previousSearchValues.route,
          division,
          success: false,
          errorText: error.message || 'An unexpected error occurred',
        })
      );
    } finally {
      searchState.setLoading(false);
      modalState.setApiResponseModal(true);
    }
  };

  useEffect(() => {
    if (printerCookie.printer && currentPrinter !== printerCookie.printer) {
      dispatch(setCurrentPrinter(printerCookie.printer));
    }
  }, [printerCookie, currentPrinter, dispatch]);

  // Sync local isExpected state with Redux state on mount or when Redux state changes
  useEffect(() => {
    if (
      savedSearch.isExpected !== undefined &&
      savedSearch.isExpected !== expectedState.isExpected
    ) {
      // Ensure we only set boolean values
      const booleanValue = Boolean(savedSearch.isExpected);
      expectedState.setIsExpected(booleanValue);
    }
  }, [savedSearch.isExpected, expectedState]);

  // Combine loading states for cleaner logic
  const isAnyLoading = isLoading || searchState.loading;

  return (
    <Box p={2}>
      {/* Global loading backdrop */}
      {isAnyLoading && (
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={isAnyLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <HeaderBox>
        <H1>{'Route Search'}</H1>
        <Button
          variant="outlined"
          startIcon={<Clear />}
          onClick={handleClearAll}
          disabled={isProductsLoading}
          sx={{ color: '#00529f', borderColor: '#00529f' }}
        >
          Clear
        </Button>
      </HeaderBox>

      <SearchBox>
        {isAnyLoading ? (
          <Box spacing={1} mt="10px" mb="10px">
            <Skeleton variant="rectangular" height="70px" />
          </Box>
        ) : (
          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            columnGap={3}
            rowGap={2}
            mt="10px"
            mb="10px"
            alignItems="center"
          >
            <NumberField
              value={searchState.route}
              setValue={searchState.setRoute}
              labelText={'Route'}
              onKeyDown={handleRouteKeyDown}
            />
            <DateField
              value={searchState.date}
              setValue={searchState.setDate}
              labelText={'Delivery Date:'}
            />
            {routeNeedToReturnQuantity != null &&
              routeReturnedQuantity != null && (
                <Typography sx={{ whiteSpace: 'nowrap' }}>
                  Piece Counter: {routeReturnedQuantity} /{' '}
                  {routeNeedToReturnQuantity}
                </Typography>
              )}
            {!isFirstLoad && !!previousSearchValues.route && (
              <Typography sx={{ whiteSpace: 'nowrap' }}>
                Unexpected Pieces:{' '}
                {RTRNCounts.AddOnCount +
                  RTRNCounts.CrossLoadCount +
                  RTRNCounts.OverLoadCount}
              </Typography>
            )}
            <Box sx={{ ml: { sm: 'auto' }, minWidth: 160 }}>
              <Button
                className="apply-btn"
                variant="contained"
                size="large"
                disabled={isProductsLoading || isApplyButtonDisabled}
                onClick={() => searchProducts(true)}
              >
                {isProductsLoading ? (
                  <CircularProgress color="inherit" size="1.75em" />
                ) : (
                  'Apply'
                )}
              </Button>
            </Box>
          </Stack>
        )}
      </SearchBox>

      <Box>
        <Box borderBottom="1px solid #A3A3A3" mb={2}>
          {isAnyLoading ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              ml={6}
              mr={6}
              mb="10px"
            >
              <Box display="flex" flexWrap="wrap" alignItems="center">
                <Checkbox
                  checked={expectedState.isExpected}
                  disabled={true}
                  onChange={handleIsExpected}
                />
                <P2>{'Expected Only'}</P2>
              </Box>
              <Paper
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 215,
                  border: '1px solid #D9D9D9',
                  boxShadow: 'none',
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  inputProps={{ 'aria-label': '' }}
                  variant="outlined"
                  value={searchState.searchInput}
                  onChange={(e) => {
                    searchState.setSearchInput(e.target.value);
                    dispatch(resetFilterList());
                    dispatch(searchOrders(e.target.value));
                  }}
                  disabled={true}
                />
                <SearchOutlined
                  sx={{
                    p: '10px',
                    color: !productList.length > 0 ? '#888888' : 'inherit',
                  }}
                />
              </Paper>
            </Stack>
          ) : (
            <Stack
              direction="row"
              justifyContent="space-between"
              ml={2}
              mr={1}
              gap={1}
              mb="10px"
            >
              <Box display="flex" flexWrap="wrap" alignItems="center">
                <Checkbox
                  checked={expectedState.isExpected}
                  disabled={isProductsLoading}
                  onChange={handleIsExpected}
                />
                <P2>{'Expected Only'}</P2>
              </Box>
              {!isProductsLoading && (
                <>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    flexGrow={0}
                    gap={1}
                    sx={{ maxWidth: '160px' }}
                  >
                    <P2>
                      {printerCookie.printer
                        ? printerCookie.printer
                        : 'No Printer Selected'}
                    </P2>
                    <Button
                      variant="contained"
                      onClick={handleOpenPrinterModal}
                      disabled={!warehouse}
                    >
                      Change Printer
                    </Button>
                  </Box>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="flex-end"
                    gap={1}
                    mr={1}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpenPrintRouteConfirmation}
                      disabled={
                        isFirstLoad ||
                        !warehouse ||
                        !currentPrinter ||
                        productList.length === 0 ||
                        !hasExpectedSkus
                      }
                    >
                      Print Expected
                    </Button>
                  </Box>
                  {IsRouteOpen(routeStatus) && !productError ? (
                    <>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-end"
                        gap={1}
                      >
                        <Button
                          variant="contained"
                          onClick={handleOpenUnexpectedItemModal}
                          disabled={
                            isFirstLoad ||
                            !IsRouteOpen(routeStatus) ||
                            !previousSearchValues.route ||
                            isProductsLoading
                          }
                        >
                          Unexpected Item
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        flex
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          mt: 1,
                          mr: 1,
                          pt: 1,
                          px: 1,
                          backgroundColor: '#FFEDCC',
                          borderRadius: '5px',
                        }}
                      >
                        <Typography
                          align="center"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {productError
                            ? `${productError}. No actions may be taken.`
                            : `This Route is closed. No further actions may be taken.\nUser: ${closeEmployeeName} (${closeEmployeeNumber}) - Date: ${closeTimestamp}`}
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              )}
              <Paper
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 215,
                  border: '1px solid #D9D9D9',
                  boxShadow: 'none',
                  height: 'fit-content',
                  alignSelf: 'flex-end',
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  inputProps={{ 'aria-label': '' }}
                  variant="outlined"
                  value={searchState.searchInput}
                  onChange={(e) => {
                    searchState.setSearchInput(e.target.value);
                    dispatch(resetFilterList());
                    dispatch(searchOrders(e.target.value));
                  }}
                  disabled={isFirstLoad || isProductsLoading}
                />
                <SearchOutlined
                  sx={{
                    p: '10px',
                    color: !productList.length > 0 ? '#888888' : 'inherit',
                  }}
                />
              </Paper>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            mb: '1rem',
            minHeight: succesfulLoad ? 80 : 57,
          }}
        >
          {isAnyLoading ? (
            <Box
              mb={4}
              sx={{
                backgroundColor: '#F5F5F5',
                minHeight: '57px',
              }}
            >
              <Skeleton variant="rectangular" height="57px" />
            </Box>
          ) : (
            <Collapse
              collapsedSize={
                isFirstLoad ? 57 : productList.length === 0 ? 80 : 'auto'
              }
              in={succesfulLoad}
            >
              <CollapsibleTable
                productList={productList}
                isFirstLoad={isFirstLoad}
                isProductsLoading={isProductsLoading}
                searchProducts={refreshProducts}
                previousSearchValues={previousSearchValues}
              />
            </Collapse>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <Button
            disabled={
              !IsRouteOpen(routeStatus) ||
              isFirstLoad ||
              !!productError ||
              isProductsLoading ||
              !previousSearchValues.route ||
              isAnyLoading
            }
            variant="contained"
            onClick={handleOpenRouteConfirmation}
            sx={{ mb: 7 }}
          >
            Close Route
          </Button>
        </Box>
      </Box>

      {/* Modals */}
      {modalState.openPrinterModal ? (
        <ChangePrinter
          isOpen={modalState.openPrinterModal}
          handleClose={handleClosePrinterModal}
          submitChanges={handleSubmitPrinterChange}
        />
      ) : null}
      {modalState.openUnexpectedItemModal ? (
        <UnexpectedItem
          isOpen={modalState.openUnexpectedItemModal}
          handleClose={handleCloseUnexpectedItemModal}
          submitChanges={handleSubmitUnexpectedItem}
        />
      ) : null}
      {modalState.apiResponseModal ? (
        <ApiResponseModal
          isOpen={modalState.apiResponseModal}
          message={apiResponseState.apiResponseMessage}
          handleClose={() => {
            modalState.setApiResponseModal(false);
            apiResponseState.setApiResponseMessage(null);
          }}
        />
      ) : null}
      {modalState.closeRouteConfirmation ? (
        <ConfirmationModal
          isOpen={modalState.closeRouteConfirmation}
          handleClose={handleCloseRouteConfirmation}
          submitChanges={() => handleConfirmCloseRoute()}
        />
      ) : null}
      {modalState.printRouteConfirmation ? (
        <ConfirmationModal
          isOpen={modalState.printRouteConfirmation}
          handleClose={handleClosePrintRouteConfirmation}
          submitChanges={handleConfirmPrintRoute}
          title="Print Route"
          message="Are you sure you want to print the expected items for this route?"
        />
      ) : null}
      {modalState.forceCloseModal ? (
        <ForceCloseModal
          isOpen={modalState.forceCloseModal}
          handleClose={handleForceModalClose}
          handleSubmit={handleTryForceClose}
          message={apiResponseState.forceCloseMessage}
          allowForce={apiResponseState.allowForceClose}
          isSuccessful={apiResponseState.isForceCloseSuccessful}
        />
      ) : null}
    </Box>
  );
}

MainPage.propTypes = {
  productList: PropTypes.arrayOf(
    PropTypes.shape({
      orderNumber1: PropTypes.number,
      salesOrderNumber: PropTypes.string,
      SalesOrderNumber: PropTypes.string,
      OrderNumber1: PropTypes.number,
      SkuData: PropTypes.arrayOf(
        PropTypes.shape({
          orderNumber2: PropTypes.number,
          OrderNumber2: PropTypes.number,
          quantityExpected: PropTypes.number,
          SKU: PropTypes.string,
          SkuNumber: PropTypes.string,
          vendorDescription: PropTypes.string,
          VendorDescription: PropTypes.string,
          orderQuantity: PropTypes.number,
          acceptQuantity: PropTypes.number,
          rejectQuantity: PropTypes.number,
        })
      ),
    })
  ),
  addSKUList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sku: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
  isLoading: PropTypes.bool.isRequired,
};
